<template>
    <Overlay
        class="received-request-workflow"
        :show="isRequestLoading"
    >
        <BAlert
            v-if="runtimeError && !pageRequest"
            variant="danger"
            show
        >
            <div class="alert-body">
                {{ runtimeError.message || runtimeError }}
            </div>
        </BAlert>

        <template v-if="pageRequest">
            <RequestLifeCycle
                ref="requestLifeCycle"
                :life-cycle="pageRequest.lifeCycle || []"
            />

            <BCard id="simple-flow">
                <BRow class="mb-1 align-items-center">
                    <BCol class="d-flex align-items-center">
                        <StatusBadge
                            type="received"
                            :status="pageRequest.status"
                        />
                        <h2 class="mb-0 ml-1">
                            {{ pageTitle }}
                        </h2>
                    </BCol>
                    <BCol cols="auto">
                        <Actions
                            :request="pageRequest"
                            :show-save="false"
                            :show-reply="false"
                            :show-forward="false"
                            :is-life-cycle-visible="(pageRequest.lifeCycle || []).length > 0"
                            life-cycle-button-variant="outline-primary"
                            @showLifeCycle="showLifeCycle"
                            @close="close"
                        />
                    </BCol>
                </BRow>

                <BRow>
                    <BCol>
                        <RequestOverview :request="pageRequest" />
                    </BCol>
                </BRow>

                <BAlert
                    v-if="backgroundError"
                    variant="warning"
                    show
                    class="mt-1 mb-0"
                >
                    <div class="alert-body">
                        {{ backgroundError.message || backgroundError }}
                    </div>
                </BAlert>

                <BAlert
                    v-if="runtimeError"
                    variant="danger"
                    show
                    class="mt-1 mb-0"
                >
                    <div class="alert-body">
                        {{ runtimeError.message || runtimeError }}
                    </div>
                </BAlert>

                <WorkflowHost
                    v-if="runner && context"
                    :runner="runner"
                    @runtime-error="handleRuntimeError"
                />
                <div
                    v-else-if="!runtimeError"
                    class="d-flex justify-content-center py-3"
                    data-testid="workflow-bootstrap-loading"
                >
                    <BSpinner variant="primary" />
                </div>
            </BCard>
        </template>
    </Overlay>
</template>

<script>
import {
    BAlert,
    BCard,
    BCol,
    BRow,
    BSpinner,
} from 'bootstrap-vue'
import { receivedRequestProvider } from '@core/domain/product'
import Overlay from '@core/components/common/Overlay.vue'
import {
    Actions,
} from '@/views/request/received/components'
import StatusBadge from '@/views/request/components/StatusBadge.vue'
import RequestLifeCycle from '@/views/request/components/RequestLifeCycle.vue'
import WorkflowHost from './components/WorkflowHost.vue'
import { RequestOverview } from './overview'
import { cellcombManifest } from './manifests'
import { createReceivedRequestWorkflowContext } from './context/create-received-request-workflow-context'
import { createReceivedRequestStepRegistry } from './registry/create-received-request-step-registry'
import { createReceivedRequestConditionRegistry } from './registry/create-received-request-condition-registry'
import { compileFlowManifest } from './runtime/manifest-compiler'
import { bootstrapWorkflowRunner } from './runtime/bootstrap-workflow-runner'
import { createLegacyDoneStepsProgressRepository } from './runtime/progress'

const waitForBrowserPaint = () => {
    if (
        typeof window === 'undefined'
        || typeof window.requestAnimationFrame !== 'function'
    ) {
        return new Promise(resolve => setTimeout(resolve, 0))
    }

    return new Promise(resolve => {
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(resolve)
        })
    })
}

const defaultRuntimeDependencies = Object.freeze({
    receivedRequestProvider,
    createContext: createReceivedRequestWorkflowContext,
    createStepRegistry: createReceivedRequestStepRegistry,
    createConditionRegistry: createReceivedRequestConditionRegistry,
    compileFlowManifest,
    bootstrapWorkflowRunner,
    createProgressRepository: createLegacyDoneStepsProgressRepository,
    waitForPaint: waitForBrowserPaint,
})

export default {
    name: 'ReceivedRequestWorkflow',
    components: {
        BAlert,
        BCard,
        BCol,
        BRow,
        BSpinner,
        Actions,
        Overlay,
        RequestLifeCycle,
        StatusBadge,
        RequestOverview,
        WorkflowHost,
    },
    props: {
        id: {
            type: [String, Number],
            required: true,
        },
        manifest: {
            type: Object,
            default: () => cellcombManifest,
        },
        initialRequest: {
            type: Object,
            default: null,
        },
        mode: {
            type: String,
            default: 'single',
            validator: value => ['single', 'bulk'].includes(value),
        },
        additionalRequestIds: {
            type: Array,
            default: () => [],
        },
        runtimeDependencies: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            context: null,
            runner: null,
            runtimeError: null,
            backgroundError: null,
        }
    },
    computed: {
        pageRequest() {
            return (this.context && this.context.request) || this.initialRequest
        },
        isRequestLoading() {
            return this.pageRequest === null
        },
        pageTitle() {
            const request = this.pageRequest
            const productNames = request.product && Array.isArray(request.product.names)
                ? request.product.names
                : []
            const product = productNames.length > 0 ? productNames[0].value : ''
            const customer = request.customer && request.customer.title ? request.customer.title : ''

            return this.$t('Page(ReceivedRequest):Component(Text):page-label', { customer, product })
        },
    },
    created() {
        this.initialize()
    },
    methods: {
        async initialize() {
            this.runtimeError = null

            const dependencies = {
                ...defaultRuntimeDependencies,
                ...this.runtimeDependencies,
            }

            try {
                // Cached providers can resolve their whole Promise chain before the browser
                // paints. Keep the request shell visible for one frame before bootstrap work.
                await dependencies.waitForPaint()

                if (this._isBeingDestroyed || this._isDestroyed) {
                    return
                }

                const context = await dependencies.createContext({
                    requestId: this.id,
                    mode: this.mode,
                    additionalRequestIds: this.additionalRequestIds,
                    updateAdditionalRequestIds: this.updateAdditionalRequestIds,
                    receivedRequestProvider: dependencies.receivedRequestProvider,
                    forwardRequest: this.forwardRequest,
                    openSentRequest: this.openSentRequest,
                    declarationApprovalProvider: this.createDeclarationApprovalProvider(),
                })
                this.context = context
                const stepRegistry = dependencies.createStepRegistry()
                const conditionRegistry = dependencies.createConditionRegistry()
                const compiledFlow = dependencies.compileFlowManifest(this.manifest, {
                    stepRegistry,
                    conditionRegistry,
                })
                const progressRepository = dependencies.createProgressRepository({
                    requestId: this.id,
                    receivedRequestProvider: dependencies.receivedRequestProvider,
                })

                this.runner = await dependencies.bootstrapWorkflowRunner({
                    compiledFlow,
                    context,
                    progressRepository,
                })
                this.$emit('ready', this.runner)

                // Let the host and active step render before cached background resources
                // start mutating larger reactive collections such as substances.
                await dependencies.waitForPaint()

                if (this._isBeingDestroyed || this._isDestroyed) {
                    return
                }

                this.initializeBackgroundData(context, this.runner)
            } catch (error) {
                this.runtimeError = error
                this.$emit('runtime-error', error)
            }
        },
        handleRuntimeError(error) {
            this.runtimeError = error
            this.$emit('runtime-error', error)
        },
        initializeBackgroundData(context, runner) {
            const initialize = context.services && context.services.initializeBackgroundData

            if (typeof initialize !== 'function') {
                return
            }

            this.backgroundError = null
            return initialize()
                .then(
                    () => runner.updateContext(context),
                    error => {
                        this.backgroundError = error
                        this.$emit('background-error', error)

                        return runner.updateContext(context)
                    },
                )
                .catch(error => this.handleRuntimeError(error))
        },
        createDeclarationApprovalProvider() {
            const profile = this.$store.getters.profile || {}

            return {
                getState: () => Promise.resolve({
                    required: this.$store.getters.subscription.isActive === false,
                    approved: this.$store.getters.isDeclarationApproved,
                    scopeId: this.$store.getters.organization.id || null,
                    identity: {
                        name: profile.name || '',
                        email: profile.email || '',
                    },
                }),
                markApproved: ({ scopeId }) => this.$store.dispatch('declarationApproved', scopeId),
            }
        },
        showLifeCycle() {
            this.$refs.requestLifeCycle.show()
        },
        close() {
            this.$emit('close')

            if (this.$router) {
                this.$router.push({ name: 'received-requests' })
            }
        },
        forwardRequest(request) {
            if (!this.$router) {
                return
            }

            if (this.$store && this.$store.getters.showSendEmailSettingsStep) {
                this.$router.push({
                    name: 'request-email-options',
                    query: {
                        n: 'forward-request',
                        nid: this.id,
                        b: 'received-request',
                        bid: this.id,
                    },
                })
                return
            }

            this.$router.push({
                name: 'forward-request',
                params: { id: request.id, req: request },
                props: { request },
            })
        },
        openSentRequest(request) {
            if (!this.$router || !request || !request.id) {
                return
            }

            this.$router.push({
                name: 'sent-request',
                params: { id: request.id },
            })
        },
        updateAdditionalRequestIds(requestIds) {
            if (!this.$router || !this.$route) {
                return Promise.resolve()
            }

            const query = { ...this.$route.query }
            if (requestIds.length > 0) {
                query.additionalRequestIds = requestIds.join(',')
            } else {
                delete query.additionalRequestIds
            }

            return Promise.resolve(this.$router.replace({ query })).catch(() => undefined)
        },
    },
}
</script>

<style lang="scss">
.received-request-workflow {
    --workflow-secondary-text-color: #45434c;

    .text-less-muted,
    .text-muted {
        color: var(--workflow-secondary-text-color) !important;
    }
}
</style>
